<script setup>
import {ref, computed, onMounted, watch, reactive, readonly} from "vue";
import { useI18n } from "vue-i18n";
import { handleError } from '@/utils/errors.js';
import ButtonNew from "@/components/common/button/ButtonNew.vue";
import { useRouter } from "vue-router";
import {computeClass} from "@/utils/styleFormats.js";
import BaseTable from "@/components/tables/BaseTable.vue";
import {ProductEntity} from "@/entities/ProductEntity.js";
import BaseQuickSearch from "@/components/common/Filters/BaseQuickSearch.vue";
import BaseAdvancedFilter from "@/components/common/Filters/BaseAdvancedFilter.vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import {getProducts} from "@/services/api/products.js";
import {getAvailableActionsForRoute} from "@/services/api/permissionManagement.js";
import {formatDate, formatDateByLocale} from "@/utils/dateTimeFormat.js";
import {formatNumber} from "@/utils/numberFormat.js";
import {searchAndSetOptions} from "@/utils/index.js";
import {getCities} from "@/services/api/city.js";

const { t, locale } = useI18n();
const router = useRouter();
const productEntity = new ProductEntity();
const entity = ref(productEntity);
const items = ref([]);
const totalItems = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const searchText = ref("");
const filters = ref([]);
const quickSearchConfig = computed(() => entity.value.pages.list.filters.quickSearch);
const actions = ref({ view: "view" });
const havePermission = ref(false);
const isFetching = ref(false);
const orderBy = ref(null);

async function fetchData(params = {}) {
    try {
        params = params.value ?? params;
        loading.value = true;
        currentPage.value = params.page ?? currentPage.value;
        if (orderBy.value) {
            params['orderBy'] = orderBy.value.key;
        }
        if (searchText.value && searchText.value.trim() !== "") {
            const field = quickSearchConfig.value.searchBy[0];
            params[field] = `%${searchText.value}%`;
        }

        let finalParams = formatFilters(params);

        if (filters.value && Object.keys(filters.value).length > 0) {
            finalParams = {
                ...filters.value,
                ...finalParams
            };
        }

        const response = await getProducts({
            limit: itemsPerPage.value,
            page: currentPage.value,
            ...finalParams
        });

        items.value = response.data.items ?? response.data;
        if (Array.isArray(items.value)) {
            items.value = items.value.map(item => ({
                ...item,
                price: formatNumber(item.price, locale.value) || null,
                st_product_service: item.st_product_service === "S" ? t("components.contracts.product.service") :
                    item.st_product_service === "P" ? t("components.contracts.product.product") :  "",
            }));
        }

        totalItems.value = response.page;
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        loading.value = false;
    }
}

function formatFilters(filters) {
    const result = { ...filters };
    const formattedStart = result.start_date ? formatDate(result.start_date) : null;
    const formattedEnd = result.end_date ? formatDate(result.end_date) : null;

    if (result.number) {
        result.number = `${result.number}`;
    }

    if(searchText.value){
        result.name = `%${searchText.value}%`;
    }

    if(result.city_id){
        result.city_id = result.city_id.value;
    }

    if (formattedStart && formattedEnd) {
        result.start_date = `${formattedStart}|${formattedEnd}`;
        result.end_date = '';
    } else if (formattedStart) {
        result.start_date = `${formattedStart}|${formattedStart}`;
    } else if (formattedEnd) {
        result.start_date = `${formattedEnd}|${formattedEnd}`;
    }

    return result;
}
async function handleSearch({ handler, query }) {
    const handlers = {
        cityList: () =>
            searchAndSetOptions({
                key: "city_id",
                apiFn: getCities,
                advancedFilter: true,
                query,
                entity,
                isFetching
            }),
    };

    if (handlers[handler]) {
        await handlers[handler]();
    } else {
        console.warn(`Handler não encontrado: ${handler}`);
    }
}

function onView(item) {
    router.push(entity.value.getUrl(entity.value.pages.view.route?.at(-1), { id: item.id }));
}

async function onPageChange(page) {
    await fetchData({page: page});
}


async function onAdvancedFilter(filtersAdvanced) {
    filters.value = formatFilters(filtersAdvanced);
    await fetchData(filtersAdvanced);
}

async function onCleanFilter() {
    filters.value = null;
    await fetchData();
}

async function onOrderBy(orderByArray) {
    orderBy.value = orderByArray;
}

async function onQuickSearch(search) {
    searchText.value = search;
    await fetchData();
}


onMounted(async () => {
    await fetchData();
})
</script>

<template>
    <BasePage :entity="entity">
        <template #default>
            <v-row class="mb-4">
                <v-col>
                    <!-- new button-->
                </v-col>
                <v-col class="base-advanced-filter">
                    <BaseAdvancedFilter
                        :entity="entity"
                        @search="handleSearch"
                        @filter="onAdvancedFilter"
                        @clean="onCleanFilter"
                    />
                </v-col>
                <v-col class="base-quick-search">
                    <BaseQuickSearch
                        :entity="entity"
                        v-if="quickSearchConfig"
                        :config="quickSearchConfig"
                        v-model="searchText"
                        :fetchData="fetchData"
                    />
                </v-col>
            </v-row>

            <BaseTable
                :fetchData="fetchData"
                :entity="entity"
                :items="items"
                :loading="loading"
                :totalItems="totalItems"
                :currentPage="currentPage"
                :itemsPerPage="itemsPerPage"
                :actions="actions"
                :tooltipText="t('common.messages.info.defaultCannotbeChanged', {name: t(entity.entityName)})"
                @view="onView"
                @page-change="onPageChange"
                @onOrderBy="onOrderBy"
            >
                <template #status="{ row }">
                    <span class="trezo-status-badge" :class="computeClass(row.status)" >
                        {{ t('common.labels.' + (row.status ? 'active' : 'inactive' )) }}
                    </span>
                </template>
            </BaseTable>
        </template>
    </BasePage>
</template>
